import { Typography, useMediaQuery, Box, Button, Alert, AlertTitle, Collapse } from '@mui/material';
import { useTheme } from '@mui/styles';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LinearLoader from '@/components/LinearLoader';
import { useEffect, useState } from 'react';
import { push } from '@/utils/matomoUtils';
import { REGULAR_FLOW_SCREEN_NAMES } from '@/constants/eventTrackingConstants';
import {
  Site,
  SiteSource,
  TrackUserEventMutation,
  TrackUserEventMutationVariables,
} from '@/gql/graphql';
import { TrackingEventsConstants } from '@/constants';
import { useMutation } from '@apollo/client';
import { TRACK_USER_EVENT } from '@/mutations';
import ConfirmSiteDialog from '@/components/ConfirmSiteDialog';
import { GraphQLErrors } from '@apollo/client/errors';

//For now it's only used on this duplicates screen but can be externalized if we need to reuse it elsewhere
const SiteCard = ({
  site,
  notSvApp = false,
  onDismiss = false,
  onSelected,
  loading = false,
}: {
  site: Site;
  notSvApp?: boolean;
  onDismiss?: false | (() => void);
  onSelected: () => void;
  loading?: boolean;
}) => {
  const isSmallScreen = useMediaQuery('(max-width:414px)');

  return (
    <Box
      mb={3}
      sx={{
        border: (theme) => `2px solid ${theme.palette.grey[200]}`,
        borderRadius: 1,
        padding: '35.5px 32px',
      }}>
      <Box display="flex">
        <Box display="flex" alignItems="center" mr={1.5}>
          <LocationOnOutlinedIcon />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {site.name}
          </Typography>
          <Typography variant="body1">{site.organization}</Typography>
          <Typography variant="body2">
            {site.address1}
            {site.address2 ? ` , ${site.address2}, ` : ', '}
            {site.location?.city}, {site.location?.stateProvince}, {site.location?.postalCode}
          </Typography>
        </Box>
      </Box>

      <Box
        display="flex"
        justifyContent="center"
        flexDirection={isSmallScreen ? 'column' : 'row'}
        mt={4}>
        {onDismiss && !notSvApp && (
          <Button variant="text" color="info" onClick={onDismiss} disabled={loading}>
            This is not my site
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={() => {
            onSelected();
          }}
          sx={{ marginLeft: onDismiss ? 1.5 : 0, marginTop: isSmallScreen ? 2 : 0 }}>
          {notSvApp ? 'This is my site' : 'This is my site, I need access'}
        </Button>
      </Box>
    </Box>
  );
};

interface DuplicatesScreenProps {
  similarSites: Site[];
  onDismiss: () => void;
  onSelected: (usn: string, notSvApp: boolean) => void;
  loading?: boolean;
  errors?: GraphQLErrors;
}

const DuplicatesScreen = ({
  similarSites,
  onDismiss,
  onSelected,
  loading = false,
  errors,
}: DuplicatesScreenProps) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [viewMoreSites, setViewMoreSites] = useState(false);
  const [selectSite, setSelectSite] = useState<Site | undefined>(undefined);
  const [alertOpen, setAlertOpen] = useState(Boolean(errors));

  const notSvApp = Boolean(
    similarSites &&
      similarSites.filter(
        (s) => ![SiteSource.SvApp, SiteSource.McDirect].includes(s.source as SiteSource)
      ).length > 0
  );

  const [fireTrackEvent, { loading: trackEventLoading }] = useMutation<
    TrackUserEventMutation,
    TrackUserEventMutationVariables
  >(TRACK_USER_EVENT);

  useEffect(() => {
    push(['trackPageView', REGULAR_FLOW_SCREEN_NAMES.displayDuplicates]);
  }, []);

  if (loading) {
    return <LinearLoader />;
  }

  const handleConfirmSelectSite = async (site: Site | undefined) => {
    if (site) {
      await fireTrackEvent({
        variables: {
          eventType: TrackingEventsConstants.SV_CLICK_EVENT,
          eventValue: 'This is my site',
          trackingId: null,
          url: null,
        },
      });
      onSelected(
        site.usn,
        ![SiteSource.SvApp, SiteSource.McDirect].includes(site.source as SiteSource)
      );
      setSelectSite(undefined);
    }
  };

  return (
    <>
      <Box>
        <Box textAlign="center" mt={mdUp ? 9 : 4} mb={2}>
          {errors && (
            <Collapse in={alertOpen}>
              <Alert
                severity="error"
                sx={{
                  mb: 1,
                  border: `1px solid ${theme.palette.warning.main}`,
                  background: '#FFC7C7',
                  color: theme.palette.grey[1000],
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiAlert-icon': {
                    padding: 0,
                  },
                }}
                onClose={() => setAlertOpen(false)}>
                <AlertTitle sx={{ mt: '0.35em' }}>{errors[0].message}</AlertTitle>
              </Alert>
            </Collapse>
          )}
          <Typography
            variant="h3"
            sx={{ fontWeight: 500, color: (theme) => theme.palette.grey[800] }}>
            {notSvApp
              ? 'Good news! We may have found a match for your site.'
              : 'Good news! It looks like your site may already use SiteVault.'}
          </Typography>
        </Box>

        <Box textAlign="center" mb={similarSites.length > 1 ? 2 : 3}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 500, color: (theme) => theme.palette.grey[800] }}>
            {notSvApp
              ? 'Choose your specific site or department below.'
              : 'Choose your site below if you have SiteVault and require access.'}
          </Typography>
        </Box>

        {similarSites.length > 1 && (
          <Box textAlign="center" mb={3}>
            <Button
              variant="text"
              color="info"
              onClick={async () => {
                await fireTrackEvent({
                  variables: {
                    eventType: TrackingEventsConstants.SV_CLICK_EVENT,
                    eventValue: 'I need to add a new site',
                    trackingId: null,
                    url: null,
                  },
                });
                onDismiss();
              }}
              disabled={loading || trackEventLoading}>
              {notSvApp ? 'I need to add a new site or department' : 'I need to add a new site'}
            </Button>
          </Box>
        )}

        <Box maxWidth={616} width="100%" margin="0 auto">
          {similarSites.map((site, idx) => {
            if (viewMoreSites && idx > 5) return;
            if (!viewMoreSites && idx > 2) return;
            return (
              <SiteCard
                loading={loading || trackEventLoading}
                onDismiss={similarSites.length === 1 ? onDismiss : false}
                site={site}
                notSvApp={notSvApp}
                onSelected={async () => {
                  if (similarSites && similarSites.length === 1) {
                    // Don't show modal if there's only 1 result
                    await handleConfirmSelectSite(site);
                  } else {
                    setSelectSite(site);
                  }
                }}
              />
            );
          })}

          {similarSites.length > 1 ? (
            <Box textAlign="center" mb={3}>
              <>
                {viewMoreSites || similarSites.length < 4 ? (
                  <Button
                    variant="text"
                    color="info"
                    onClick={async () => {
                      await fireTrackEvent({
                        variables: {
                          eventType: TrackingEventsConstants.SV_CLICK_EVENT,
                          eventValue: 'My site is not listed',
                          trackingId: null,
                          url: null,
                        },
                      });
                      onDismiss();
                    }}
                    disabled={loading || trackEventLoading}>
                    My site is not listed
                  </Button>
                ) : (
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      setViewMoreSites(true);
                    }}>
                    See more sites
                  </Button>
                )}
              </>
            </Box>
          ) : (
            <Box textAlign="center" mb={3}>
              <Button
                variant="text"
                color="info"
                onClick={async () => {
                  await fireTrackEvent({
                    variables: {
                      eventType: TrackingEventsConstants.SV_CLICK_EVENT,
                      eventValue: 'I need to add a new site',
                      trackingId: null,
                      url: null,
                    },
                  });
                  onDismiss();
                }}
                disabled={loading || trackEventLoading}>
                {notSvApp ? 'I need to add a new site or department' : 'I need to add a new site'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <ConfirmSiteDialog
        open={Boolean(selectSite)}
        site={selectSite}
        loading={loading || trackEventLoading}
        onClose={() => setSelectSite(undefined)}
        onConfirm={async () => {
          await handleConfirmSelectSite(selectSite);
        }}
      />
    </>
  );
};

export default DuplicatesScreen;
